import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api.ts';
import { Event } from '../types/index.ts';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { MapPin, Users, Plus, ArrowLeft, Navigation } from 'lucide-react';
import { toast } from 'sonner';

interface Meetup {
  meetup_id: string;
  event_id: string;
  creator_name: string;
  location: string;
  description: string;
  time: string;
  attendees: number;
}

export default function VenueMeetups() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<Event | null>(null);
  const [meetups, setMeetups] = useState<Meetup[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    location: '',
    description: '',
    time: '',
  });

  useEffect(() => {
    if (eventId) {
      loadData();
    }
  }, [eventId]);

  const loadData = async () => {
    try {
      const [eventData, meetupsData] = await Promise.all([
        api.getEvent(eventId!),
        api.getVenueMeetups(eventId!)
      ]);
      setEvent(eventData);
      setMeetups(meetupsData);
    } catch (error) {
      toast.error('Failed to load meetups');
    }
  };

  const handleCreateMeetup = async () => {
    if (!formData.location || !formData.time) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await api.createVenueMeetup(eventId!, formData);
      toast.success('Meetup created successfully!');
      setIsCreating(false);
      setFormData({ location: '', description: '', time: '' });
      await loadData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to create meetup');
    }
  };

  const handleJoinMeetup = async (meetupId: string) => {
    try {
      await api.joinMeetup(meetupId);
      toast.success('Joined meetup!');
      await loadData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to join meetup');
    }
  };

  if (!event) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-20 md:pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/discover')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold">{event.title}</h1>
            <p className="text-gray-400">Venue Meetups</p>
          </div>
        </div>
        <Button onClick={() => setIsCreating(!isCreating)} className="shadow-glow">
          <Plus className="h-4 w-4 mr-2" />
          Create Meetup
        </Button>
      </div>

      {/* Create Meetup Form */}
      {isCreating && (
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle>Create a Venue Meetup</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Meeting Location *</label>
              <Input
                placeholder="e.g., Main entrance, VIP lounge, Bar area"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="glass border-white/10"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Time *</label>
              <Input
                type="datetime-local"
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                className="glass border-white/10"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <Textarea
                placeholder="What's the meetup about?"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="glass border-white/10"
              />
            </div>

            <div className="flex space-x-2">
              <Button onClick={handleCreateMeetup} className="flex-1 shadow-glow">
                Create Meetup
              </Button>
              <Button onClick={() => setIsCreating(false)} variant="outline" className="flex-1 glass-hover">
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Venue Map Placeholder */}
      <Card className="glass border-white/10">
        <CardContent className="p-6">
          <div className="aspect-video bg-background-subtle rounded-lg flex items-center justify-center">
            <div className="text-center">
              <MapPin className="h-16 w-16 text-primary mx-auto mb-2" />
              <p className="text-gray-400">Venue: {event.venue}</p>
              <p className="text-sm text-gray-500 mt-2">Map integration coming soon</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Meetups List */}
      <div className="space-y-4">
        <h2 className="text-2xl font-heading font-bold">Active Meetups</h2>
        {meetups.length > 0 ? (
          meetups.map((meetup) => (
            <Card key={meetup.meetup_id} className="glass border-white/10">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-5 w-5 text-primary" />
                      <h3 className="font-heading font-bold text-lg">{meetup.location}</h3>
                    </div>
                    <p className="text-gray-400">{meetup.description}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span>By {meetup.creator_name}</span>
                      <span>•</span>
                      <span>{meetup.time}</span>
                      <span>•</span>
                      <div className="flex items-center space-x-1">
                        <Users className="h-4 w-4" />
                        <span>{meetup.attendees} attending</span>
                      </div>
                    </div>
                  </div>
                  <Button onClick={() => handleJoinMeetup(meetup.meetup_id)} className="shadow-glow">
                    <Navigation className="h-4 w-4 mr-2" />
                    Join
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card className="glass border-white/10 p-12 text-center">
            <Users className="h-16 w-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg mb-4">No meetups yet</p>
            <p className="text-gray-500 mb-6">Be the first to create a meetup at the venue!</p>
            <Button onClick={() => setIsCreating(true)} className="shadow-glow">
              <Plus className="h-4 w-4 mr-2" />
              Create First Meetup
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
}
